import { AxiosError } from 'axios';
import moment from 'moment-timezone';
import { Decimal } from './Decimal';
import jwt_decode from "jwt-decode";
import { getCookie } from 'typescript-cookie';

export const DATE_FORMAT = 'YYYY-MM-DD';
export const TIME_FORMAT = 'HH:mm:ss';
export const TIME_HH_MM_FORMAT = 'HH:mm';
export const DATETIME_FORMAT = `${DATE_FORMAT} ${TIME_FORMAT}`;
export const AMERICAN_DATE_FORMAT = 'MM/DD/YYYY';
export const AMERICAN_DATE_DOT_FORMAT = 'MM.DD.YYYY';
export const DEFAULT_TIMEZONE = 'UTC';
export const EXP_FORMAT = 'MM/YY';


////////////////
// Date Utils
////////////////

export function getTimezone(): string {
    return moment.tz('America/New_York').format('Z');
}

export function getDaysDiff(date1: string, date2: string) {
    return Math.abs(moment(date2).diff(moment(date1), 'days'));
}

export function timestampToDate(ts: number): string {
    return moment.unix(ts / 1000).format(DATE_FORMAT);
}

export function today(): string {
    return moment().utcOffset(getTimezone()).format('YYYY-MM-DD');
}

export function getMomentObj(date: string): moment.Moment {
    return moment(date);
}

export function incrementDateByDays(byDays: number, date: string): string {
    return moment(date).add(byDays, 'days').format(DATE_FORMAT);
}

export function decremenDateByDays(byDays: number, date: string): string {
    return moment(date).subtract(byDays, 'days').format(DATE_FORMAT);
}

export function incrementDateByMonths(byMonths: number, date: string): string {
    return moment(date).add(byMonths, 'months').format(DATE_FORMAT);
}

export function decrementDateByMonths(byMonths: number, date: string): string {
    return moment(date).subtract(byMonths, 'months').format(DATE_FORMAT);
}

export function getThisWeeksWeekDay(dayInWeek: number): string {
    return moment().utcOffset(getTimezone()).isoWeekday(dayInWeek).format(DATE_FORMAT);
}

export function getLastWeeksWeekDay(dayInWeek: number): string {
    return moment().utcOffset(getTimezone()).subtract(1, 'weeks').isoWeekday(dayInWeek).format(DATE_FORMAT);
}

export function getFirstDayOfMonth(date: string): string {
    return moment(date).startOf('month').format(DATE_FORMAT);
}

export function getLastDayOfMonth(date: string): string {
    return moment(date).endOf('month').format(DATE_FORMAT);
}

export function convertDateToAmericanFormat(date: string): string {
    return moment(date, DATE_FORMAT).format(AMERICAN_DATE_FORMAT);
}

export function convertDateToAmericanDotFormat(date: string): string {
    return moment(date, DATE_FORMAT).format(AMERICAN_DATE_DOT_FORMAT);
}

export function convertDateFromAmericanDotFormat(date: string): string {
    return moment(date, AMERICAN_DATE_DOT_FORMAT).format(DATE_FORMAT);
}

export function tConvert(time: any): string {
    if (!time) {
        return '';
    }
    // Check correct time format and split into components
    time = time.toString().match(/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/) || [time];

    if (time.length > 1) {
        // If time format correct
        time = time.slice(1); // Remove full string match value
        time[5] = +time[0] < 12 ? 'AM' : 'PM'; // Set AM/PM
        time[0] = +time[0] % 12 || 12; // Adjust hours
        time[3] = ' '; // convert seconds to space
    }
    return time.join(''); // return adjusted time or original string
}

export function timeToHoursAndMinutes(time: string): string {
    const date = `1970-01-01 ${time}`;
    return moment(date, DATETIME_FORMAT).format(TIME_HH_MM_FORMAT);
}

export function incrementTimeBySeconds(bySeconds: number, time: string): string {
    const date = `1970-01-01 ${time}`;
    return moment(date).add(bySeconds, 'seconds').format(TIME_FORMAT);
}

export function datetimeFormatToAmericanFormat(datetime: string): string {
    const datetimeSplit = datetime.split(' ');
    return `${convertDateToAmericanFormat(datetimeSplit[0])} ${tConvert(datetimeSplit[1])}`;
}

export function datetimeFormatFromAmericanDotFormat(datetime: string): string {
    const datetimeSplit = datetime.split(' ');
    return `${convertDateFromAmericanDotFormat(datetimeSplit[0])} ${tConvert(datetimeSplit[1])}`;
}

export function todayUtc() {
    return moment().utc().format(DATE_FORMAT);
}
export function nowUtcTime() {
    return moment().utc().format(TIME_FORMAT);
}

export function downloadCsvFile(content: string, fileName: string) {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    // @ts-ignore
    if (navigator && navigator.msSaveBlob) {
        // IE 10+
        // @ts-ignore
        navigator.msSaveBlob(blob, fileName);
    } else {
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', fileName);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
}

export function currentWeek() {
    const dates = [];
    dates.push(getThisWeeksWeekDay(1));
    dates.push(getThisWeeksWeekDay(7));
    return dates;
}

export function lastWeek() {
    const dates = [];
    dates.push(getLastWeeksWeekDay(1));
    dates.push(getLastWeeksWeekDay(7));
    return dates;
}

export function currentMonth() {
    const dates = [];
    dates.push(getFirstDayOfMonth(today()));
    dates.push(getLastDayOfMonth(today()));
    return dates;
}

export function lastMonth() {
    const dates = [];
    dates.push(getFirstDayOfMonth(decrementDateByMonths(1, today())));
    dates.push(getLastDayOfMonth(decrementDateByMonths(1, today())));
    return dates;
}

export function last30Days() {
    const dates = [];
    dates.push(decremenDateByDays(30, today()));
    dates.push(decremenDateByDays(1, today()));
    return dates;
}

export function toDatetime(expDate: string) {
    const formatted = moment(expDate,"MM YY").format("YYYY-MM-DDThh:mm:ss")
    return formatted;

}

export function fromDatetimeToCC(expDate: string) {
    const formatted = moment(expDate,"YYYY-MM-DDThh:mm:ss").format("MM YY")
    return formatted;

}

export function getFirstDayOfYear(date: string): string {
    return moment(date).startOf('year').format(DATE_FORMAT);
}

export function getLastDayOfYear(date: string): string {
    return moment(date).endOf('year').format(DATE_FORMAT);
}

export function decrementDateByYears(byYears: number, date: string): string {
    return moment(date).subtract(byYears, 'years').format(DATE_FORMAT);
}

export function downloadFile(content: any, fileName: string, fileType: string) {
    const blob = new Blob([content], { type: `${fileType};charset=utf-8;` });
    // @ts-ignore
    if (navigator && navigator.msSaveBlob) {
        // IE 10+
        // @ts-ignore
        navigator.msSaveBlob(blob, fileName);
    } else {
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', fileName);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
}

////////////////
//Strings Utils
////////////////

export function firstCharToUpperCase(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1);
}

export function parseError(error: unknown): string {
    if(typeof error === 'string') return error;
    else if((error as AxiosError).isAxiosError && (error as AxiosError).response) {
        return (error as AxiosError).response?.data?.message || (error as AxiosError).response?.data;
    }
    else if(error instanceof Error) {
        return error.message;
    }
    return 'Error has occurred'
}

export function formatCost(cost: number, curr: string): string {
    if(!cost) {
        return '';
    }
    const currency = curr ? <Currency>curr : Currency.USD;
    const costFixed = new Decimal(cost).toFixed(2);
    const symbol = currencyToSymbol.get(currency);
    return `${symbol}${costFixed}`;
}

export function replaceSpacesWithPercent(word: string) {
    return word.replace(/\s+/g, '%');
}

export enum Currency {
    USD = 'USD',
    GBP = 'GBP',
}

const currencyToSymbol = new Map<Currency, string>([
    [Currency.USD, '&dollar;'],
    [Currency.GBP, '&pound;'],
]);

export function convertDateToExpFormat(date: string): string {
    return moment(date, DATE_FORMAT).format(EXP_FORMAT);
}

export function convertExpFormatToDate(date: string): string {
    return moment(date, EXP_FORMAT).endOf('month').format(DATE_FORMAT);
}

export function randomCchaiId(): string {
    return (Math.floor(Math.random() * 90000) + 10000).toString();
}

export function swapElements<T>(arr: T[], indexA: number, indexB: number): void {
    let temp = arr[indexA];
    arr[indexA] = arr[indexB];
    arr[indexB] = temp;
}

export function getUserIdFromToken() {
    const token = getCookie('token');
    if (!token) return null;
    const decoded = jwt_decode(token);
    // @ts-ignore
    return decoded?.userId!;
}
